/**
 * Bluesky drafts ("stash") service — `app.bsky.draft.*`.
 *
 * Calls go through the user's own PDS via the OAuth-session `Agent`, which pipethrough-proxies
 * them to its configured AppView. No Mackinac server involvement, no service-auth tokens.
 *
 * ## Lossless-Only Editing
 *
 * `app.bsky.draft` is a closed schema that Mackinac supports only a subset of: the composer is
 * single-post and persists text plus portable embeds only. Reading a draft, mapping it into that
 * reduced view model, and writing it back necessarily discards everything outside the subset —
 * including drafts the user created in the official Bluesky app, on this device or another.
 *
 * So Mackinac may `updateDraft`/`deleteDraft` ONLY on drafts currently within the supported-shape
 * whitelist below. Which device or app created a draft plays no part in that decision.
 *
 * Everything else is read-only: listed, previewed, and offered "Import a copy", which creates a
 * NEW draft on explicit save and never touches the source.
 *
 * This is a best-effort policy, not a guarantee — see the TOCTOU note on `checkDraftFreshness`.
 */

import { UnicodeString, AppBskyDraftCreateDraft } from '@atproto/api'
import type { Agent, AppBskyDraftDefs } from '@atproto/api'
import type {
    BskyDraft,
    BskyDraftInput,
    BskyDraftRecordRef,
    DraftClassification,
    DraftFreshness,
    DroppedFeature,
} from '@/types/drafts'

/** Lexicon limits on `app.bsky.draft.defs#draftPost.text`. */
export const DRAFT_TEXT_MAX_LENGTH = 10_000
export const DRAFT_TEXT_MAX_GRAPHEMES = 1_000

/** Page size for `getDrafts`. */
export const DRAFTS_PAGE_SIZE = 25

/**
 * Human-readable device label written on create.
 *
 * DISPLAY AND DEBUGGING ONLY. `deviceName` is unauthenticated, client-set metadata — any client
 * can write any value into it, and the value survives editing in another client. It is NOT proof
 * of origin. Never read it in a guard, a conditional, or a test that decides whether a draft may
 * be mutated; use `isOwnedDraft`/the ownership registry for that.
 */
export const MACKINAC_DEVICE_NAME = 'Mackinac Web'

// --- localStorage keys ---

const DEVICE_ID_KEY = 'mackinac.bskyDrafts.deviceId'
const ownedRegistryKey = (did: string) => `mackinac.bskyDrafts.owned.${did}`
const pendingCleanupKey = (did: string) => `mackinac.bskyDrafts.pendingCleanup.${did}`

/**
 * Every storage access is wrapped so that unavailable, partitioned, or quota-exhausted
 * storage (Safari private mode, storage partitioning, a thrown SecurityError) degrades
 * editability rather than breaking draft creation or the composer.
 */
function safeReadStringArray(key: string): string[] {
    try {
        const raw = window.localStorage.getItem(key)
        if (!raw) return []
        const parsed = JSON.parse(raw)
        if (!Array.isArray(parsed)) return []
        return parsed.filter((v): v is string => typeof v === 'string')
    } catch {
        // Fail closed: an unreadable registry means "we do not own anything".
        return []
    }
}

function safeWriteStringArray(key: string, values: string[]): void {
    try {
        window.localStorage.setItem(key, JSON.stringify(values))
    } catch {
        // Non-fatal. A registry write failure costs future editability, never the draft itself.
    }
}

// --- Device id ---

/** UUIDv4 identifying this browser profile. Persisted; regenerated if storage is unavailable. */
export function getDeviceId(): string {
    try {
        const existing = window.localStorage.getItem(DEVICE_ID_KEY)
        if (existing) return existing
        const generated = crypto.randomUUID()
        window.localStorage.setItem(DEVICE_ID_KEY, generated)
        return generated
    } catch {
        return crypto.randomUUID()
    }
}

// --- Ownership registry ---

/** Draft ids Mackinac created for this DID on this browser profile. */
export function readOwnedRegistry(did: string): Set<string> {
    return new Set(safeReadStringArray(ownedRegistryKey(did)))
}

export function addOwnedDraftId(did: string, id: string): void {
    const owned = readOwnedRegistry(did)
    owned.add(id)
    safeWriteStringArray(ownedRegistryKey(did), [...owned])
}

export function removeOwnedDraftId(did: string, id: string): void {
    const owned = readOwnedRegistry(did)
    if (!owned.delete(id)) return
    safeWriteStringArray(ownedRegistryKey(did), [...owned])
}

/**
 * Drop registry entries whose ids no longer appear on the server, so the registry does not grow
 * without bound.
 *
 * Deliberately conservative: only call this after a SUCCESSFUL full-list read. A transient
 * network failure must never silently strip ownership from drafts that still exist.
 */
export function pruneOwnedRegistry(did: string, liveIds: Iterable<string>): void {
    const live = new Set(liveIds)
    const owned = readOwnedRegistry(did)
    const kept = [...owned].filter(id => live.has(id))
    if (kept.length === owned.size) return
    safeWriteStringArray(ownedRegistryKey(did), kept)
}

// --- Pending delete-after-publish cleanup ---

export interface PendingCleanupEntry {
    id: string
    /**
     * The draft's `updatedAt` when cleanup was scheduled. Absent only if the caller itself had
     * no `knownUpdatedAt` to persist — mirrored on read as "no freshness check is possible",
     * the same fallback `cleanupPublishedDraft`'s first attempt uses in that case.
     */
    updatedAt?: string
}

function isPendingCleanupEntry(value: unknown): value is PendingCleanupEntry {
    return (
        isRecord(value) &&
        typeof value.id === 'string' &&
        (value.updatedAt === undefined || typeof value.updatedAt === 'string')
    )
}

/**
 * An awaited promise inside the publish success path does not survive the tab being closed, so
 * "awaited" is not "durable". Pending cleanup entries — an id plus the `updatedAt` known at the
 * time — are persisted before the delete is attempted and retried the next time the drafts hook
 * mounts, so the retry can run the same freshness check the first attempt does.
 */
export function readPendingCleanupIds(did: string): PendingCleanupEntry[] {
    try {
        const raw = window.localStorage.getItem(pendingCleanupKey(did))
        if (!raw) return []
        const parsed = JSON.parse(raw)
        if (!Array.isArray(parsed)) return []
        return parsed.filter(isPendingCleanupEntry)
    } catch {
        // Fail closed: an unreadable pending list means "nothing to retry".
        return []
    }
}

function writePendingCleanupEntries(did: string, entries: PendingCleanupEntry[]): void {
    try {
        window.localStorage.setItem(pendingCleanupKey(did), JSON.stringify(entries))
    } catch {
        // Non-fatal. A write failure costs the mount-retry, never the delete itself.
    }
}

export function addPendingCleanupId(did: string, id: string, updatedAt?: string): void {
    const pending = readPendingCleanupIds(did).filter(entry => entry.id !== id)
    pending.push({ id, updatedAt })
    writePendingCleanupEntries(did, pending)
}

export function removePendingCleanupId(did: string, id: string): void {
    const pending = readPendingCleanupIds(did)
    const filtered = pending.filter(entry => entry.id !== id)
    if (filtered.length === pending.length) return
    writePendingCleanupEntries(did, filtered)
}

// --- Supported-shape whitelist ---

/*
 * These are WHITELISTS, checked as real key-set comparisons rather than as tests for specific
 * known-bad fields. That distinction is the whole point: when an SDK upgrade adds a field to the
 * lexicon, a draft carrying it must cost editability (falling back to import-a-copy) rather than
 * having the new field silently dropped on the next update.
 *
 * Keys split into two groups:
 *   - structural keys Mackinac reads and can round-trip
 *   - keys Mackinac understands but cannot represent, which must be absent or empty and which
 *     produce their own specific DroppedFeature kind (so import copy can name them)
 * Anything outside both groups yields `unknownFields`.
 */

const DRAFT_KNOWN_KEYS = new Set([
    '$type',
    'deviceId',
    'deviceName',
    'posts',
    // understood-but-unrepresentable
    'langs',
    'postgateEmbeddingRules',
    'threadgateAllow',
])

const DRAFT_POST_KNOWN_KEYS = new Set([
    '$type',
    'text',
    'embedExternals',
    'embedRecords',
    // understood-but-unrepresentable
    'labels',
    'embedImages',
    'embedVideos',
])

const EXTERNAL_KNOWN_KEYS = new Set(['$type', 'uri'])
const RECORD_KNOWN_KEYS = new Set(['$type', 'record'])
const STRONG_REF_KNOWN_KEYS = new Set(['$type', 'uri', 'cid'])

const QUOTABLE_COLLECTION = 'app.bsky.feed.post'

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function collectUnknownKeys(
    obj: Record<string, unknown>,
    known: ReadonlySet<string>,
    pathPrefix: string
): string[] {
    return Object.keys(obj)
        .filter(key => !known.has(key))
        .map(key => `${pathPrefix}.${key}`)
}

function checkTypeTag(
    obj: Record<string, unknown>,
    expected: string,
    pathPrefix: string
): string[] {
    const tag = obj.$type
    if (tag === undefined || tag === expected) return []
    return [`${pathPrefix}.$type`]
}

function nonEmptyArray(value: unknown): unknown[] | null {
    return Array.isArray(value) && value.length > 0 ? value : null
}

function isAbsoluteHttpUrl(value: unknown): boolean {
    if (typeof value !== 'string' || value.length === 0) return false
    try {
        const parsed = new URL(value)
        return parsed.protocol === 'http:' || parsed.protocol === 'https:'
    } catch {
        return false
    }
}

/** `at://<authority>/<collection>/<rkey>` → collection, or null if not a well-formed at:// URI. */
function atUriCollection(value: unknown): string | null {
    if (typeof value !== 'string' || !value.startsWith('at://')) return null
    const segments = value.slice('at://'.length).split('/')
    if (segments.length < 3) return null
    return segments[1] || null
}

/**
 * Classify a stash draft against the supported-shape whitelist.
 *
 * `ownedIds` decides only the `owned` field, kept for debugging/telemetry — it is not a factor
 * in `editable`. Which device or app created a draft plays no part in the Open-vs-Import
 * decision; only whether its current shape is fully representable does.
 *
 * A pure function of its inputs, re-evaluated on every read — editability is never cached or
 * trusted from a previous session, so a draft that gains an image out-of-band stops being
 * editable on the very next fetch.
 */
export function classifyDraft(
    view: AppBskyDraftDefs.DraftView,
    ownedIds: ReadonlySet<string>
): DraftClassification {
    const owned = ownedIds.has(view.id)
    const losses: DroppedFeature[] = []
    const unknownPaths: string[] = []

    const draft = isRecord(view.draft) ? view.draft : null
    if (!draft) {
        return {
            editable: false,
            owned,
            droppedFeatures: [{ kind: 'unknownFields', paths: ['draft'] }],
        }
    }

    unknownPaths.push(...collectUnknownKeys(draft, DRAFT_KNOWN_KEYS, 'draft'))
    unknownPaths.push(...checkTypeTag(draft, 'app.bsky.draft.defs#draft', 'draft'))

    if (nonEmptyArray(draft.langs)) losses.push({ kind: 'languages' })
    if (nonEmptyArray(draft.threadgateAllow)) losses.push({ kind: 'threadgateRules' })
    if (nonEmptyArray(draft.postgateEmbeddingRules)) losses.push({ kind: 'postgateRules' })

    const posts = Array.isArray(draft.posts) ? draft.posts : null
    if (!posts || posts.length === 0) {
        unknownPaths.push('draft.posts')
    } else {
        if (posts.length > 1) {
            losses.push({ kind: 'additionalPosts', count: posts.length - 1 })
        }
        losses.push(...classifyDraftPost(posts[0], unknownPaths))
    }

    if (unknownPaths.length > 0) {
        losses.push({ kind: 'unknownFields', paths: unknownPaths })
    }

    return { editable: losses.length === 0, owned, droppedFeatures: losses }
}

function classifyDraftPost(post: unknown, unknownPaths: string[]): DroppedFeature[] {
    const losses: DroppedFeature[] = []
    if (!isRecord(post)) {
        unknownPaths.push('draft.posts[0]')
        return losses
    }

    unknownPaths.push(...collectUnknownKeys(post, DRAFT_POST_KNOWN_KEYS, 'draft.posts[0]'))
    unknownPaths.push(...checkTypeTag(post, 'app.bsky.draft.defs#draftPost', 'draft.posts[0]'))

    if (post.labels !== undefined) losses.push({ kind: 'selfLabels' })

    const images = nonEmptyArray(post.embedImages)
    if (images) losses.push({ kind: 'images', count: images.length })

    const videos = nonEmptyArray(post.embedVideos)
    if (videos) losses.push({ kind: 'video' })

    const externals = nonEmptyArray(post.embedExternals)
    if (externals) {
        if (externals.length > 1) {
            losses.push({ kind: 'surplusExternalEmbeds', count: externals.length - 1 })
        }
        losses.push(...classifyExternalEmbed(externals[0], unknownPaths))
    }

    const records = nonEmptyArray(post.embedRecords)
    if (records) {
        if (records.length > 1) {
            losses.push({ kind: 'surplusRecordEmbeds', count: records.length - 1 })
        }
        losses.push(...classifyRecordEmbed(records[0], unknownPaths))
    }

    return losses
}

function classifyExternalEmbed(embed: unknown, unknownPaths: string[]): DroppedFeature[] {
    const path = 'draft.posts[0].embedExternals[0]'
    if (!isRecord(embed)) {
        unknownPaths.push(path)
        return []
    }
    unknownPaths.push(...collectUnknownKeys(embed, EXTERNAL_KNOWN_KEYS, path))
    unknownPaths.push(...checkTypeTag(embed, 'app.bsky.draft.defs#draftEmbedExternal', path))
    if (!isAbsoluteHttpUrl(embed.uri)) return [{ kind: 'malformedExternalUri' }]
    return []
}

function classifyRecordEmbed(embed: unknown, unknownPaths: string[]): DroppedFeature[] {
    const path = 'draft.posts[0].embedRecords[0]'
    if (!isRecord(embed)) {
        unknownPaths.push(path)
        return []
    }
    unknownPaths.push(...collectUnknownKeys(embed, RECORD_KNOWN_KEYS, path))
    unknownPaths.push(...checkTypeTag(embed, 'app.bsky.draft.defs#draftEmbedRecord', path))

    const ref = embed.record
    const refPath = `${path}.record`
    if (!isRecord(ref)) {
        unknownPaths.push(refPath)
        return []
    }
    unknownPaths.push(...collectUnknownKeys(ref, STRONG_REF_KNOWN_KEYS, refPath))
    unknownPaths.push(...checkTypeTag(ref, 'com.atproto.repo.strongRef', refPath))

    if (typeof ref.cid !== 'string' || ref.cid.length === 0) {
        return [{ kind: 'recordEmbedMissingCid' }]
    }
    // A record embed pointing at a list, feed generator, or starter pack is not something
    // Mackinac's composer can represent as a quote.
    if (atUriCollection(ref.uri) !== QUOTABLE_COLLECTION) {
        return [{ kind: 'unsupportedEmbedTarget' }]
    }
    return []
}

// --- Mappers ---

/** Map a stash `DraftView` into the composer's view model. Post 1 only; media never restored. */
export function mapDraftViewToBskyDraft(
    view: AppBskyDraftDefs.DraftView,
    ownedIds: ReadonlySet<string>
): BskyDraft {
    const classification = classifyDraft(view, ownedIds)
    const post = view.draft?.posts?.[0]

    const externalEmbed = post?.embedExternals?.[0]
    const externalUri =
        externalEmbed && isAbsoluteHttpUrl(externalEmbed.uri) ? externalEmbed.uri : null

    const recordEmbed = post?.embedRecords?.[0]?.record
    const recordRef: BskyDraftRecordRef | null =
        recordEmbed &&
        typeof recordEmbed.uri === 'string' &&
        typeof recordEmbed.cid === 'string' &&
        recordEmbed.cid.length > 0
            ? { uri: recordEmbed.uri, cid: recordEmbed.cid }
            : null

    return {
        id: view.id,
        text: typeof post?.text === 'string' ? post.text : '',
        createdAt: view.createdAt,
        updatedAt: view.updatedAt,
        externalUri,
        recordRef,
        deviceName: view.draft?.deviceName,
        classification,
    }
}

/** Map composer state into a stash `Draft`. Never emits `embedImages`/`embedVideos`. */
export function composerStateToDraft(input: BskyDraftInput): AppBskyDraftDefs.Draft {
    const post: AppBskyDraftDefs.DraftPost = { text: input.text }

    if (input.externalUri) {
        post.embedExternals = [{ uri: input.externalUri }]
    }
    // com.atproto.repo.strongRef requires cid. A quote without one (e.g. a Mastodon quote) is
    // omitted entirely rather than emitted with a fabricated or empty cid.
    if (input.quote?.uri && input.quote.cid) {
        post.embedRecords = [{ record: { uri: input.quote.uri, cid: input.quote.cid } }]
    }

    return {
        deviceId: getDeviceId(),
        // Display/debug only — never a mutability guard. See MACKINAC_DEVICE_NAME.
        deviceName: MACKINAC_DEVICE_NAME,
        posts: [post],
    }
}

// --- Save-time validation ---

export class DraftTextTooLongError extends Error {
    // Declared as fields rather than constructor parameter properties: the build config
    // enables `erasableSyntaxOnly`, which rejects the shorthand.
    readonly limit: 'characters' | 'graphemes'
    readonly actual: number
    readonly max: number

    constructor(limit: 'characters' | 'graphemes', actual: number, max: number) {
        super(`Draft text exceeds the ${max} ${limit} limit (${actual}).`)
        this.name = 'DraftTextTooLongError'
        this.limit = limit
        this.actual = actual
        this.max = max
    }
}

/**
 * Enforce the lexicon's text limits before the call, so a violation surfaces as a specific
 * client-side message rather than a generic server rejection. Never truncates.
 *
 * Grapheme counting uses `UnicodeString` from `@atproto/api` — the same primitive the official
 * client validates with. NOTE: the SDK's `UnicodeString`/`RichText` are unrelated to Mackinac's
 * own `@/components/RichText` rendering component. Do not conflate or alias them.
 */
export function validateDraftText(text: string): void {
    if (text.length > DRAFT_TEXT_MAX_LENGTH) {
        throw new DraftTextTooLongError('characters', text.length, DRAFT_TEXT_MAX_LENGTH)
    }
    const graphemes = new UnicodeString(text).graphemeLength
    if (graphemes > DRAFT_TEXT_MAX_GRAPHEMES) {
        throw new DraftTextTooLongError('graphemes', graphemes, DRAFT_TEXT_MAX_GRAPHEMES)
    }
}

// --- Errors ---

/** The draft changed on another device between classification and the mutation. */
export class DraftChangedElsewhereError extends Error {
    readonly draftId: string

    constructor(draftId: string) {
        super('This draft was updated somewhere else.')
        this.name = 'DraftChangedElsewhereError'
        this.draftId = draftId
    }
}

/** The draft no longer exists on the server. Treated as already-deleted, never as an error. */
export class DraftAlreadyDeletedError extends Error {
    readonly draftId: string

    constructor(draftId: string) {
        super('That draft was already deleted somewhere else.')
        this.name = 'DraftAlreadyDeletedError'
        this.draftId = draftId
    }
}

/**
 * `createDraft` exports a typed `DraftLimitReachedError`, but the call reaches the AppView
 * through PDS pipethrough proxying, which can flatten it into a generic `XRPCError` carrying
 * only the stable code string. Match the code string first; keep `instanceof` as a fallback.
 */
export function isDraftLimitReachedError(err: unknown): boolean {
    const code = (err as { error?: unknown } | null | undefined)?.error
    if (typeof code === 'string' && code === 'DraftLimitReached') return true
    return err instanceof AppBskyDraftCreateDraft.DraftLimitReachedError
}

// --- API ---

export interface BskyDraftsPage {
    drafts: BskyDraft[]
    cursor?: string
}

/** Fetch one page of drafts, classified against the ownership registry. */
export async function fetchDraftsPage(
    agent: Agent,
    did: string,
    cursor?: string
): Promise<BskyDraftsPage> {
    const { data } = await agent.app.bsky.draft.getDrafts({
        limit: DRAFTS_PAGE_SIZE,
        ...(cursor ? { cursor } : {}),
    })
    const owned = readOwnedRegistry(did)
    return {
        // Server order is preserved deliberately — no client-side sort.
        drafts: data.drafts.map(view => mapDraftViewToBskyDraft(view, owned)),
        cursor: data.cursor,
    }
}

/** Maximum pages walked when looking up a single draft (the lexicon has no getDraft-by-id). */
const DRAFT_LOOKUP_MAX_PAGES = 10

/** Find one draft's current server state. Returns null when it no longer exists. */
export async function fetchDraftView(
    agent: Agent,
    id: string
): Promise<AppBskyDraftDefs.DraftView | null> {
    let cursor: string | undefined
    for (let page = 0; page < DRAFT_LOOKUP_MAX_PAGES; page++) {
        const { data } = await agent.app.bsky.draft.getDrafts({
            limit: DRAFTS_PAGE_SIZE,
            ...(cursor ? { cursor } : {}),
        })
        const match = data.drafts.find(view => view.id === id)
        if (match) return match
        if (!data.cursor) return null
        cursor = data.cursor
    }
    return null
}

/**
 * Pre-mutation freshness check (mitigation for the accepted TOCTOU race).
 *
 * `app.bsky.draft` has NO `rev`, NO `etag`, NO `cid` on the draft, and no conditional-write
 * parameter — verified against the full lexicon schema. There is therefore no compare-and-swap
 * available, and `updatedAt` is the only change signal a client has.
 *
 * Refetching and comparing `updatedAt` immediately before mutating narrows the window to the
 * final network round-trip. It does NOT close it: the same user editing the same draft in the
 * official Bluesky app within that window can still be clobbered. This residual race is a
 * property of the API, is shared by the official client, and is accepted as a known limitation
 * rather than claimed solved. Do not describe Lossless-Only Editing as a guarantee.
 *
 * Throws on network failure — callers must not proceed blind on a stale classification.
 */
export async function checkDraftFreshness(
    agent: Agent,
    id: string,
    knownUpdatedAt: string
): Promise<DraftFreshness> {
    const view = await fetchDraftView(agent, id)
    if (!view) return 'missing'
    return view.updatedAt === knownUpdatedAt ? 'fresh' : 'changed'
}

/** Create a new draft and record its id in the ownership registry. */
export async function createDraft(
    agent: Agent,
    did: string,
    input: BskyDraftInput
): Promise<string> {
    validateDraftText(input.text)
    const { data } = await agent.app.bsky.draft.createDraft({
        draft: composerStateToDraft(input),
    })
    addOwnedDraftId(did, data.id)
    return data.id
}

/**
 * Update an existing draft.
 *
 * The id nests INSIDE the wrapper: `{ draft: { id, draft } }`. Flattening it to siblings does
 * not match `app.bsky.draft.updateDraft`'s input schema and will be rejected.
 */
export async function updateDraft(agent: Agent, id: string, input: BskyDraftInput): Promise<void> {
    validateDraftText(input.text)
    await agent.app.bsky.draft.updateDraft({
        draft: { id, draft: composerStateToDraft(input) },
    })
}

/** Delete a draft and drop it from the ownership registry. */
export async function deleteDraft(agent: Agent, did: string, id: string): Promise<void> {
    await agent.app.bsky.draft.deleteDraft({ id })
    removeOwnedDraftId(did, id)
}
